import React, { Fragment, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Row, Col, Card, Modal, Form, Button } from 'react-bootstrap';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { useLanguage } from '../../services/language/LanguageContext';
import { Link } from 'react-router-dom';
import { DateTimeFormat } from '../../helper/Formatters';
import { useUser } from '../../services/redux-services/UserInfoContext';
import Pagination from '../../components/pagination';

const StatusPill = ({ color, label }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px 3px 7px',
        background: color ? `${color}18` : '#f3f4f6',
        border: `1px solid ${color ? `${color}40` : '#e5e7eb'}`,
        borderRadius: 20, fontSize: 12, fontWeight: 600,
        color: color || '#6b7280', whiteSpace: 'nowrap',
    }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color || '#9ca3af', flexShrink: 0 }} />
        {label || '-'}
    </span>
);

const SectionLabel = ({ icon, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, marginTop: 4 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #1e3a5f18, #2d6a9f18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={icon} style={{ fontSize: 12, color: '#1e3a5f' }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
    </div>
);

const parseGoogleMapsUrl = (url) => {
    if (!url) return null;
    // Format: @lat,lng,zoom  e.g. /@13.7563,100.5018,17z
    const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch) return { lat: atMatch[1], lng: atMatch[2] };
    // Format: q=lat,lng
    const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (qMatch) return { lat: qMatch[1], lng: qMatch[2] };
    // Format: ll=lat,lng
    const llMatch = url.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (llMatch) return { lat: llMatch[1], lng: llMatch[2] };
    // Format: !3d lat !4d lng
    const dMatch = url.match(/!3d(-?\d+\.?\d*).*?!4d(-?\d+\.?\d*)/);
    if (dMatch) return { lat: dMatch[1], lng: dMatch[2] };
    return null;
};

const AVATAR_COLORS = ['#1e3a5f', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626'];
const getAvatarColor = (str = '') => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const OwnerDropdown = ({ users, value, onChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = users.filter(u =>
        `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(search.toLowerCase())
    );
    const selected = users.find(u => u.id === value) || null;
    const selInitials = selected ? `${(selected.firstName || '')[0] || ''}${(selected.lastName || '')[0] || ''}`.toUpperCase() : null;
    const selColor = selected ? getAvatarColor(selected.firstName || '') : null;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            {/* Trigger */}
            <button type="button" onClick={() => setOpen(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 12px', borderRadius: 8,
                border: `1.5px solid ${open ? '#2d6a9f' : '#dee2e6'}`,
                background: '#fff', cursor: 'pointer', textAlign: 'left',
                boxShadow: open ? '0 0 0 3px rgba(45,106,159,0.1)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
            }}>
                {selected ? (
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg, ${selColor}cc, ${selColor})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                        {selInitials}
                    </div>
                ) : (
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>
                        <i className="fa fa-user" />
                    </div>
                )}
                <span style={{ flex: 1, fontSize: 14, color: selected ? '#1f2937' : '#9ca3af' }}>
                    {selected ? `${selected.firstName || ''} ${selected.lastName || ''}`.trim() : placeholder}
                </span>
                <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#9ca3af' }} />
            </button>

            {/* Panel */}
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1060,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
                }}>
                    {/* Search */}
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ position: 'relative' }}>
                            <i className="fa fa-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 11, pointerEvents: 'none' }} />
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search..."
                                style={{ width: '100%', paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, outline: 'none', color: '#374151' }}
                            />
                        </div>
                    </div>
                    {/* Options */}
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {/* Clear option */}
                        <OwnerOption
                            onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                            isSelected={!value}
                            avatar={<i className="fa fa-minus" style={{ fontSize: 10 }} />}
                            avatarBg="#f3f4f6"
                            avatarColor="#9ca3af"
                            label={placeholder}
                            labelColor="#9ca3af"
                        />
                        {filtered.map(u => {
                            const c = getAvatarColor(u.firstName || '');
                            const ini = `${(u.firstName || '')[0] || ''}${(u.lastName || '')[0] || ''}`.toUpperCase();
                            return (
                                <OwnerOption
                                    key={u.id}
                                    onClick={() => { onChange(u.id); setOpen(false); setSearch(''); }}
                                    isSelected={u.id === value}
                                    avatar={ini}
                                    avatarBg={`linear-gradient(135deg, ${c}cc, ${c})`}
                                    avatarColor="#fff"
                                    label={`${u.firstName || ''} ${u.lastName || ''}`.trim()}
                                />
                            );
                        })}
                        {filtered.length === 0 && (
                            <div style={{ padding: '16px 12px', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                                <i className="fa fa-search" style={{ marginRight: 6, opacity: 0.5 }} />No results
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const OwnerOption = ({ onClick, isSelected, avatar, avatarBg, avatarColor, label, labelColor }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button type="button" onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '100%', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10,
                background: isSelected ? '#eff6ff' : hovered ? '#f9fafb' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
            }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: avatarBg, color: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                {avatar}
            </div>
            <span style={{ flex: 1, fontSize: 13, color: labelColor || '#1f2937', fontWeight: isSelected ? 600 : 400 }}>{label}</span>
            {isSelected && <i className="fa fa-check" style={{ color: '#2d6a9f', fontSize: 11 }} />}
        </button>
    );
};

const Stores = () => {
    const { messages } = useSignalR();
    const api = useApiServices();
    const { user } = useUser();
    const { t } = useLanguage();
    const [data, setData] = useState({});
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [mapLink, setMapLink] = useState('');
    const [parsedCoords, setParsedCoords] = useState(null);
    const [formData, setFormData] = useState({
        name: '', latitude: '', longitude: '', radiusKm: '', sequence: 0, userId: user?.id ?? '',
    });

    const fetchData = async (page = 1, kw = keyword) => {
        const params = { pageNumber: page, pageSize: 10, keyword: kw || undefined };
        const { isSuccess, result } = await api.GetStoresAsync(params);
        if (isSuccess) setData(result);
    };

    const fetchUsers = async () => {
        const { isSuccess, result } = await api.GetUserListAsync({ pageNumber: 1, pageSize: 500 });
        if (isSuccess) setUsers(result.items);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setKeyword(searchInput);
        fetchData(1, searchInput);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'sequence' ? parseInt(value) : value }));
    };

    const handleMapLinkChange = (e) => {
        const val = e.target.value;
        setMapLink(val);
        const coords = parseGoogleMapsUrl(val);
        if (coords) {
            setParsedCoords(coords);
            setFormData(prev => ({ ...prev, latitude: coords.lat, longitude: coords.lng }));
        } else {
            setParsedCoords(null);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setMapLink('');
        setParsedCoords(null);
        setFormData({ name: '', latitude: '', longitude: '', radiusKm: '', sequence: 0, userId: user?.id ?? '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const { isSuccess } = await api.AddStoresAsync(formData);
        setSubmitting(false);
        if (isSuccess) {
            toast.success(t('create_store') + ' ' + t('active').toLowerCase());
            handleCloseModal();
            fetchData();
        } else {
            toast.error(t('no_data'));
        }
    };

    useEffect(() => { fetchData(); fetchUsers(); }, []);
    useEffect(() => {
        if (showModal) setFormData(prev => ({ ...prev, userId: user?.id ?? '' }));
    }, [showModal]);
    useEffect(() => {
        if (!messages?.length) return;
        const last = messages[messages.length - 1];
        if (last && (last.type === 'Transaction' || last.type === 'Payment') && data?.currentPage === 1) fetchData();
    }, [messages]);

    const items = data.items || [];

    return (
        <Fragment>
            <Row>
                <Col sm="12">
                    <Card style={cardStyle}>
                        <Card.Header style={cardHeaderStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={titleDotStyle} />
                                <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e3a5f' }}>{t('stores_title')}</h5>
                                {data.totalItems > 0 && <span style={countBadgeStyle}>{data.totalItems}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
                                    <div style={searchWrapStyle}>
                                        <i className="fa fa-search" style={searchIconStyle} />
                                        <input type="text" placeholder={t('search_stores')} value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)} style={searchInputStyle} />
                                    </div>
                                    {keyword && (
                                        <button type="button" style={clearBtnStyle} onClick={() => { setSearchInput(''); setKeyword(''); fetchData(1, ''); }}>
                                            <i className="fa fa-times" />
                                        </button>
                                    )}
                                </form>
                                <button onClick={() => setShowModal(true)} style={addBtnStyle}>
                                    <i className="fa fa-plus" style={{ fontSize: 10 }} />{t('add_store')}
                                </button>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: 0 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            {[t('col_store_name'), t('col_owner'), t('col_items'), t('col_status'), t('col_date')].map(h => (
                                                <th key={h} style={thStyle}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length === 0 ? (
                                            <tr><td colSpan={5} style={emptyStyle}>
                                                <i className="fas fa-store" style={{ fontSize: 24, marginBottom: 8, display: 'block', opacity: 0.3 }} />
                                                {t('no_data')}
                                            </td></tr>
                                        ) : items.map(row => (
                                            <tr key={row.id}
                                                onMouseEnter={() => setHoveredRow(row.id)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                                style={{ background: hoveredRow === row.id ? '#f9fafb' : '#fff', transition: 'background 0.1s' }}>
                                                <td style={tdStyle}><Link to={`/stores/${row.id}`} style={linkStyle}>{row.name || '-'}</Link></td>
                                                <td style={tdStyle}>
                                                    <Link to={`/users/${row.user?.id}`} style={{ ...linkStyle, fontWeight: 400, color: '#374151' }}>
                                                        {`${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim() || '-'}
                                                    </Link>
                                                </td>
                                                <td style={tdStyle}>
                                                    {row.productToStoreConfigurations?.length > 0 ? (
                                                        <span style={{ fontSize: 12, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '2px 8px', fontWeight: 600 }}>
                                                            {row.productToStoreConfigurations.length} {t('col_items')}
                                                        </span>
                                                    ) : <span style={{ fontSize: 12, color: '#9ca3af' }}>-</span>}
                                                </td>
                                                <td style={tdStyle}><StatusPill color={row.status?.color} label={row.status?.labelTh || row.status?.name} /></td>
                                                <td style={{ ...tdStyle, color: '#9ca3af', fontSize: 12 }}>{DateTimeFormat(row.createDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: '12px 20px' }}>
                                <Pagination currentPage={data.currentPage} totalPages={data.totalPages} onPageChange={(page) => fetchData(page)} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Add Store Modal ── */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-store" style={{ color: '#fff', fontSize: 14 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f' }}>{t('add_store')}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{t('store_basic_info')} · {t('location_info')}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <Form onSubmit={handleSubmit}>

                        {/* ── Basic Info ── */}
                        <SectionLabel icon="fa fa-info-circle" label={t('store_basic_info')} />
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('store_owner')}</Form.Label>
                                    <OwnerDropdown
                                        users={users}
                                        value={formData.userId}
                                        onChange={(val) => setFormData(prev => ({ ...prev, userId: val }))}
                                        placeholder={t('select_owner')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('store_name')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                    <Form.Control name="name" value={formData.name} onChange={handleChange} required placeholder={t('store_name')} style={inputStyle} />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* ── Location ── */}
                        <SectionLabel icon="fas fa-map-marker-alt" label={t('location_info')} />

                        {/* Google Maps paste */}
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>
                                <i className="fa fa-map" style={{ color: '#34a853', marginRight: 6 }} />
                                {t('google_maps_link')}
                            </Form.Label>
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                    {parsedCoords
                                        ? <i className="fa fa-check-circle" style={{ color: '#10b981', fontSize: 14 }} />
                                        : <img src="https://www.gstatic.com/images/branding/product/1x/maps_24dp.png" alt="" style={{ width: 14, height: 14, opacity: 0.7 }} onError={e => { e.target.style.display = 'none'; }} />
                                    }
                                </div>
                                <Form.Control
                                    value={mapLink}
                                    onChange={handleMapLinkChange}
                                    placeholder={t('google_maps_placeholder')}
                                    style={{ ...inputStyle, paddingLeft: 34, borderColor: parsedCoords ? '#10b981' : mapLink ? '#ef4444' : '#dee2e6' }}
                                />
                                {mapLink && (
                                    <button type="button" onClick={() => { setMapLink(''); setParsedCoords(null); }}
                                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                                        <i className="fa fa-times" style={{ fontSize: 12 }} />
                                    </button>
                                )}
                            </div>
                            {mapLink && (
                                <div style={{ fontSize: 11, marginTop: 5, display: 'flex', alignItems: 'center', gap: 5,
                                    color: parsedCoords ? '#10b981' : '#ef4444' }}>
                                    <i className={`fa ${parsedCoords ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
                                    {parsedCoords ? t('map_coords_found') : t('map_coords_not_found')}
                                </div>
                            )}
                        </Form.Group>

                        {/* Lat / Lng / Radius / Sequence */}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('latitude')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <Form.Control
                                            name="latitude" value={formData.latitude} onChange={handleChange}
                                            required placeholder="e.g. 13.7563"
                                            style={{ ...inputStyle, fontFamily: 'monospace', paddingRight: parsedCoords ? 34 : undefined, borderColor: parsedCoords ? '#10b981' : undefined }}
                                        />
                                        {parsedCoords && <i className="fas fa-magic" style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontSize: 12, pointerEvents: 'none' }} />}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('longitude')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <Form.Control
                                            name="longitude" value={formData.longitude} onChange={handleChange}
                                            required placeholder="e.g. 100.5018"
                                            style={{ ...inputStyle, fontFamily: 'monospace', paddingRight: parsedCoords ? 34 : undefined, borderColor: parsedCoords ? '#10b981' : undefined }}
                                        />
                                        {parsedCoords && <i className="fas fa-magic" style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontSize: 12, pointerEvents: 'none' }} />}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('radius_km')}</Form.Label>
                                    <Form.Control name="radiusKm" type="number" value={formData.radiusKm} onChange={handleChange} placeholder="0" style={inputStyle} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={labelStyle}>{t('sequence')}</Form.Label>
                                    <Form.Control name="sequence" type="number" value={formData.sequence} onChange={handleChange} style={inputStyle} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                            <button type="button" onClick={handleCloseModal} style={cancelBtnStyle}>{t('cancel')}</button>
                            <button type="submit" disabled={submitting} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '9px 22px',
                                background: submitting ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                                border: 'none', borderRadius: 10,
                                color: '#fff', fontSize: 14, fontWeight: 600,
                                cursor: submitting ? 'not-allowed' : 'pointer',
                                boxShadow: '0 2px 8px rgba(30,58,95,0.25)',
                            }}>
                                {submitting
                                    ? <><i className="fa fa-spinner fa-spin" /> {t('saving')}</>
                                    : <><i className="fas fa-store" /> {t('create_store')}</>
                                }
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const cardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fff', borderRadius: '12px 12px 0 0' };
const titleDotStyle = { width: 10, height: 10, borderRadius: '50%', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', flexShrink: 0 };
const countBadgeStyle = { fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' };
const searchWrapStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
const searchIconStyle = { position: 'absolute', left: 10, color: '#9ca3af', fontSize: 12, pointerEvents: 'none' };
const searchInputStyle = { paddingLeft: 30, paddingRight: 12, paddingTop: 6, paddingBottom: 6, border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 200, color: '#374151' };
const clearBtnStyle = { padding: '6px 10px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 12 };
const addBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#1f2937', verticalAlign: 'middle' };
const linkStyle = { color: '#1e3a5f', fontWeight: 600, textDecoration: 'none' };
const emptyStyle = { padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13, borderBottom: 'none' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { fontSize: 14, borderRadius: 8 };
const cancelBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' };

export default Stores;
